import {
  CalendarOutlined,
  CarOutlined,
  InboxOutlined,
  PaperClipOutlined,
  PlayCircleOutlined,
  ScheduleOutlined,
  StopOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../../../../AppContext';
import {
  DRAWER_HEADER_ACTIONS_ID,
  DRAWER_HEADER_ACTIONS_ID_MOBILE,
  ROUTES,
} from '../../../../../common/constants';
import GoBackButton from '../../../../../components/GoBackButton';
import HeaderActionsDropdown from '../../../../../components/HeaderActionsDropdown';
import Portal from '../../../../../components/Portal';
import { ULTRA_PLAN_ALLOWED_ROLES } from '../../../../ultraPlan/ultraPlanConstants';
import EndUsersTabs from '../components/EndUsersTabs';
import SubscriptionInfo from '../components/SubscriptionInfo';

const SubscriptionDetails = ({
  match: { params: { id, userNumber } = {} } = {},
  isDrawerView = false,
  // Props for drawer mode
  userId: drawerUserId,
  userNumber: drawerUserNumber,
}) => {
  const {
    state: { endUserDetails, isDesktop, currentUser },
    dispatch,
  } = useContext(AppContext);
  const history = useHistory();

  // Use drawer props if in drawer mode, otherwise use route params
  const currentId = isDrawerView ? drawerUserId : id;
  const currentUserNumber = isDrawerView ? drawerUserNumber : userNumber;

  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
  const [isHoldingModalOpen, setIsHoldingModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isExpiryDateModalOpen, setIsExpiryDateModalOpen] = useState(false);
  const [isSubscriptionBtnDisable, setIsSubscriptionBtnDisable] = useState(
    true,
  );
  const [isExpiryDateBtnDisable, setIsExpiryDateBtnDisable] = useState(true);
  const [isDeliveryBtnDisable, setIsDeliveryBtnDisable] = useState(true);
  const [isUpdatePlanModalOpen, setIsUpdatePlanModalOpen] = useState(false);
  const [isUpdatePlanBtnDisable, setIsUpdatePlanBtnDisable] = useState(true);
  const [isPlanListEmpty, setIsPlanListEmpty] = useState(false);
  const [isRoadMapModalOpen, setIsRoadMapModalOpen] = useState(false);
  const [roadMapButtonConfig, setRoadMapButtonConfig] = useState({
    isVisible: false,
    hasRoadMap: false,
    userSubscriptionId: null,
  });
  const canManageRoadmap = currentUser?.roles?.some((role) =>
    ULTRA_PLAN_ALLOWED_ROLES?.includes(role),
  );
  const [
    isCancelSubscriptionModalOpen,
    setIsCancelSubscriptionModalOpen,
  ] = useState(false);
  const [
    isCancelSubscriptionBtnDisable,
    setIsCancelSubscriptionBtnDisable,
  ] = useState(true);

  const actionItems = [
    {
      key: 'manage',
      type: 'group',
      label: 'Manage',
      children: [
        {
          key: 'activate',
          icon: <PlayCircleOutlined />,
          label: 'Activate Subscription',
          disabled: isSubscriptionBtnDisable,
          onClick: () => setIsSubscriptionModalOpen(true),
        },
        {
          key: 'update',
          icon: <SyncOutlined />,
          label: 'Update Subscription',
          disabled: isUpdatePlanBtnDisable,
          onClick: () => setIsUpdatePlanModalOpen(true),
        },
        ...(roadMapButtonConfig?.isVisible
          ? [
              {
                key: 'roadMap',
                icon: <PaperClipOutlined />,
                label: `${
                  roadMapButtonConfig?.hasRoadMap ? 'Update' : 'Attach'
                } Road Map`,
                onClick: () => setIsRoadMapModalOpen(true),
              },
              ...(canManageRoadmap && roadMapButtonConfig?.userSubscriptionId
                ? [
                    {
                      key: 'manageRoadmap',
                      icon: <ScheduleOutlined />,
                      label: 'Manage Roadmap',
                      onClick: () => {
                        if (isDrawerView) {
                          dispatch({
                            type: 'SET_END_USER_DRAWER_VISIBLE',
                            data: false,
                          });
                        }
                        history?.push(
                          `${ROUTES?.ULTRA_PLAN}${ROUTES?.ROADMAPS}/${
                            currentId || endUserDetails?.id
                          }/${roadMapButtonConfig?.userSubscriptionId}`,
                        );
                      },
                    },
                  ]
                : []),
            ]
          : []),
      ],
    },
    {
      key: 'edit',
      type: 'group',
      label: 'Edit',
      children: [
        {
          key: 'expiryDate',
          icon: <CalendarOutlined />,
          label: 'Expiry Date',
          disabled: isExpiryDateBtnDisable,
          onClick: () => setIsExpiryDateModalOpen(true),
        },
        {
          key: 'freeDeliveries',
          icon: <CarOutlined />,
          label: 'Free Deliveries',
          disabled: isDeliveryBtnDisable,
          onClick: () => setIsDeliveryModalOpen(true),
        },
        {
          key: 'toyHoldings',
          icon: <InboxOutlined />,
          label: 'Toy Holdings',
          onClick: () => setIsHoldingModalOpen(true),
        },
      ],
    },
    { type: 'divider' },
    {
      key: 'cancel',
      icon: <StopOutlined />,
      label: 'Cancel Subscription',
      danger: true,
      disabled: isCancelSubscriptionBtnDisable,
      onClick: () => setIsCancelSubscriptionModalOpen(true),
    },
  ];

  const actionButtons = (
    <div className="header-btn-wrapper">
      <HeaderActionsDropdown items={actionItems} />
    </div>
  );

  const subscriptionInfoComponent = (
    <SubscriptionInfo
      isDeliveryModalOpen={isDeliveryModalOpen}
      setIsDeliveryModalOpen={setIsDeliveryModalOpen}
      isHoldingModalOpen={isHoldingModalOpen}
      setIsHoldingModalOpen={setIsHoldingModalOpen}
      isSubscriptionModalOpen={isSubscriptionModalOpen}
      setIsSubscriptionModalOpen={setIsSubscriptionModalOpen}
      isExpiryDateModalOpen={isExpiryDateModalOpen}
      setIsExpiryDateModalOpen={setIsExpiryDateModalOpen}
      userId={currentId || endUserDetails?.id}
      isView={false}
      setIsSubscriptionBtnDisable={setIsSubscriptionBtnDisable}
      setIsExpiryDateBtnDisable={setIsExpiryDateBtnDisable}
      setIsDeliveryBtnDisable={setIsDeliveryBtnDisable}
      isUpdatePlanModalOpen={isUpdatePlanModalOpen}
      setIsUpdatePlanModalOpen={setIsUpdatePlanModalOpen}
      setIsUpdatePlanBtnDisable={setIsUpdatePlanBtnDisable}
      isPlanListEmpty={isPlanListEmpty}
      setIsPlanListEmpty={setIsPlanListEmpty}
      isRoadMapModalOpen={isRoadMapModalOpen}
      setIsRoadMapModalOpen={setIsRoadMapModalOpen}
      setRoadMapButtonConfig={setRoadMapButtonConfig}
      isCancelSubscriptionModalOpen={isCancelSubscriptionModalOpen}
      setIsCancelSubscriptionModalOpen={setIsCancelSubscriptionModalOpen}
      setIsCancelSubscriptionBtnDisable={setIsCancelSubscriptionBtnDisable}
    />
  );

  if (isDrawerView) {
    return (
      <>
        {isDesktop ? (
          <Portal portalId={DRAWER_HEADER_ACTIONS_ID}>
            <div className="drawer-header-actions">{actionButtons}</div>
          </Portal>
        ) : (
          <Portal portalId={DRAWER_HEADER_ACTIONS_ID_MOBILE}>
            <div className="drawer-header-actions">{actionButtons}</div>
          </Portal>
        )}
        <div className="full-height-without-details">
          {subscriptionInfoComponent}
        </div>
      </>
    );
  }

  return (
    <>
      <Portal portalId="header-title-content">
        <GoBackButton
          customLink={`${ROUTES?.SYSTEM_USERS}${ROUTES?.END_USERS}`}
        />
        <div className="portal-header">Edit End User</div>
      </Portal>
      <Portal portalId="header-right-content">{actionButtons}</Portal>
      <Portal portalId="header-right-content-phones">{actionButtons}</Portal>
      <EndUsersTabs endUserId={currentId} endUserNumber={currentUserNumber}>
        <div className="full-height-without-details">
          {subscriptionInfoComponent}
        </div>
      </EndUsersTabs>
    </>
  );
};

export default SubscriptionDetails;
